import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import TableItems from 'components/UI/TableItems/TableItems';
import './TableSection.css';

const STATUS_OPTIONS = [
  { id: 'all', name: 'All' },
  { id: 'active', name: 'Active' },
  { id: 'inactive', name: 'Inactive' },
];

/**
 * Renders title action items (links or buttons) in a dropdown.
 */
const renderTitleActions = (items) => {
  if (!items || !items.length) return null;
  return items.map((item, index) => {
    if (item.divider) {
      return <Dropdown.Divider key={`divider-${index}`} />;
    }
    if (item.to) {
      const to = typeof item.to === 'string' ? { pathname: item.to } : item.to;
      return (
        <Dropdown.Item key={`item-${index}`} as={Link} to={to}>
          {item.icon ? <img src={item.icon} alt="" className="table-section-action-icon mr-2" /> : null}
          {item.label}
        </Dropdown.Item>
      );
    }
    return (
      <Dropdown.Item key={`item-${index}`} onClick={item.onClick}>
        {item.icon ? <img src={item.icon} alt="" className="table-section-action-icon mr-2" /> : null}
        {item.label}
      </Dropdown.Item>
    );
  });
};

/**
 * Generic table section with optional header, table (via tableData + tableColumns/tableHeaders), and pagination.
 */
const TableSection = ({
  title,
  titleActions = [],
  showStatusFilter = true,
  statusValue = 'all',
  onStatusChange,
  hideHeader = false,
  hideTitleAndStatusFilter = true,
  children,
  tableData,
  tableHeaders,
  tableColumns,
  tableSectionProps = {},
  pageCount = 0,
  currentPage = 1,
  onPageChange,
  isLoading = false,
  emptyMessage = 'No data found',
  titleActionsToggleLabel = 'Actions',
  className = '',
}) => {
  const hasTable = tableData && ((tableHeaders && tableHeaders.length > 0) || (tableColumns && tableColumns.length > 0));
  const content = hasTable ? (
    <TableItems
      data={tableData}
      headers={tableHeaders}
      columns={tableColumns}
      serialNo={tableSectionProps.serialNo !== false}
      srNo={tableSectionProps.srNo ?? 1}
      emptyMessage={emptyMessage}
      emptyImage={tableSectionProps.emptyImage !== false}
      className={tableSectionProps.className}
    />
  ) : (
    children
  );

  const handlePageChange = (val) => {
    if (onPageChange) onPageChange(val);
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;
    if (onStatusChange) onStatusChange(value);
  };

  const showPagination = pageCount > 0 && (hasTable || children);

  const showHeader = !hideHeader && (!hideTitleAndStatusFilter || (titleActions && titleActions.length > 0));
  const showTitle = !hideHeader && !hideTitleAndStatusFilter;
  const showStatusFilterSelect = !hideHeader && !hideTitleAndStatusFilter && showStatusFilter;

  return (
    <div className={`table-section ${className}`.trim()}>
      {showHeader && (
        <div className="table-section-header">
          {showTitle && <h4 className="table-section-title">{title}</h4>}
          <div className="table-section-header-actions">
            {showStatusFilterSelect && (
              <div className="table-section-status-filter">
                <select
                  className="table-section-status-select"
                  value={statusValue}
                  onChange={handleStatusChange}
                  aria-label="Filter by status"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {titleActions && titleActions.length > 0 && (
              <Dropdown className="table-section-actions-dropdown" alignRight>
                <Dropdown.Toggle className="btn dropdown-toggle dropdown-val dropddown_align table-section-actions-toggle">
                  {titleActionsToggleLabel}
                </Dropdown.Toggle>
                <Dropdown.Menu className="table-section-actions-menu">
                  {renderTitleActions(titleActions)}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
        </div>
      )}

      <div className="table-section-content">
        {isLoading ? (
          <div className="table-section-loading">Loading...</div>
        ) : (
          <Fragment>
            {content}
            {showPagination && (
              <div className="panel-pagination-col panel-table-pagination table-section-pagination">
                <ReactPaginate
                  previousLabel={'<'}
                  nextLabel={'>'}
                  breakLabel={'...'}
                  breakClassName={'break-me'}
                  pageCount={pageCount}
                  forcePage={currentPage - 1}
                  pageRangeDisplayed={5}
                  onPageChange={handlePageChange}
                  activeClassName={'active'}
                />
              </div>
            )}
          </Fragment>
        )}
      </div>

      {!isLoading && !content && (
        <div className="table-section-empty">{emptyMessage}</div>
      )}
    </div>
  );
};

TableSection.propTypes = {
  title: PropTypes.string,
  hideHeader: PropTypes.bool,
  hideTitleAndStatusFilter: PropTypes.bool,
  titleActions: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        onClick: PropTypes.func,
      }),
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      }),
      PropTypes.shape({
        label: PropTypes.string,
        icon: PropTypes.string,
      }),
      PropTypes.shape({ divider: PropTypes.bool }),
    ])
  ),
  showStatusFilter: PropTypes.bool,
  statusValue: PropTypes.oneOf(['all', 'active', 'inactive']),
  onStatusChange: PropTypes.func,
  children: PropTypes.node,
  tableData: PropTypes.array,
  tableHeaders: PropTypes.array,
  tableColumns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      format: PropTypes.func,
      getActions: PropTypes.func,
    })
  ),
  tableSectionProps: PropTypes.object,
  pageCount: PropTypes.number,
  currentPage: PropTypes.number,
  onPageChange: PropTypes.func,
  isLoading: PropTypes.bool,
  emptyMessage: PropTypes.node,
  titleActionsToggleLabel: PropTypes.string,
  className: PropTypes.string,
};

export default TableSection;
